import { Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { eachDayOfInterval, endOfMonth, format, startOfMonth, subDays } from "date-fns";

import { CalendarGridSectionWrapper } from "./GridSectionWrapper";

interface CalendarGridMonthProps {
  /**
   * The start date of the month to render
   * All days before this date will be rendered via `renderEmptyCell`
   */
  startDate: Date;
  renderDateCell: (date: Date) => React.ReactNode;
  /**
   * Used to render empty cells at the start of the month to align with Sunday start
   * Renders invisible cell if not provided
   */
  renderEmptyCell?: (date: Date) => React.ReactNode;
  /**
   * Used to render past dates (earlier in the month than `startDate`)
   * Renders invisible cell if not provided
   */
  renderPastCell?: (date: Date) => React.ReactNode;

  showMonthTitle?: boolean;

  weekSpacing?: number;
}

export function CalendarGridMonth(props: CalendarGridMonthProps) {
  const {
    startDate,
    renderDateCell,
    renderEmptyCell,
    renderPastCell,
    showMonthTitle = true,
    weekSpacing,
  } = props;

  const monthStart = startOfMonth(startDate);
  const monthEnd = endOfMonth(startDate);

  const allMonthDays = eachDayOfInterval({
    start: monthStart,
    end: monthEnd,
  });

  // empty cells are needed to align with Sunday start
  const emptyCellsNeeded = monthStart.getDay();
  const emptyStartCells = Array.from({ length: emptyCellsNeeded }, (_, index) =>
    subDays(monthStart, emptyCellsNeeded - index)
  );

  return (
    <Stack spacing={5}>
      {showMonthTitle && <Text variant="h5">{format(startDate, "MMM")}</Text>}

      <CalendarGridSectionWrapper sx={{ rowGap: weekSpacing ?? 3 }}>
        {emptyStartCells.map((date) => (
          <Box key={date.toISOString()} sx={{ display: "flex", justifyContent: "center" }}>
            {renderEmptyCell ? renderEmptyCell(date) : null}
          </Box>
        ))}

        {allMonthDays.map((date) => {
          const isPastDate = date < startDate;

          if (isPastDate) {
            return (
              <Box key={date.toISOString()} sx={{ display: "flex", justifyContent: "center" }}>
                {renderPastCell ? renderPastCell(date) : null}
              </Box>
            );
          }

          return (
            <Box key={date.toISOString()} sx={{ display: "flex", justifyContent: "center" }}>
              {renderDateCell(date)}
            </Box>
          );
        })}
      </CalendarGridSectionWrapper>
    </Stack>
  );
}
